import React, { createContext, useContext, useMemo } from 'react';
import type { ClassValue } from 'class-variance-authority/types';

import {
    applyOverride,
    type Recipe,
    type RecipeContextValue,
    type RecipeOverrides,
} from './recipe';

const EMPTY: RecipeContextValue = { overrides: {} };

const UIVariantsContext = createContext<RecipeContextValue>(EMPTY);
UIVariantsContext.displayName = 'UIVariantsContext';

export interface UIVariantsProviderProps {
    /**
     * Keyed by recipe name. Merged with any provider above this one, so a screen
     * can refine what the app set without restating it.
     */
    overrides?: RecipeOverrides;
    /** Adds `ui-theme-<name>` to every recipe's base. */
    theme?: string;
    children: React.ReactNode;
}

/**
 * The seam that makes new variants and themes an APP-side change.
 *
 * Without it, adding a tone or reskinning a component means editing this
 * package and cutting a release — which is the tax that pushes teams into
 * forking a component instead. With it:
 *
 * ```tsx
 * <UIVariantsProvider
 *     theme="compact"
 *     overrides={{
 *         // a tone the core has never heard of
 *         callout: { variants: { tone: { brand: 'my-callout--brand' } } },
 *         // reskin an existing one, keeping the core's layout classes
 *         chip: { variants: { tone: { stale: 'my-chip--stale' } } },
 *         // take the core's classes off entirely for this slot
 *         matrixBox: { mode: 'replace', base: 'my-box' },
 *     }}
 * >
 * ```
 *
 * Nesting MERGES rather than shadows: an inner provider that names one recipe
 * leaves the outer provider's other entries intact. Per-recipe it is a replace,
 * not a deep merge — a half-overridden variant map is harder to reason about
 * than restating the three tones you meant.
 */
export function UIVariantsProvider({
    overrides,
    theme,
    children,
}: UIVariantsProviderProps): React.ReactElement {
    const parent = useContext(UIVariantsContext);
    const value = useMemo<RecipeContextValue>(
        () => ({
            overrides: { ...parent.overrides, ...(overrides ?? {}) },
            theme: theme ?? parent.theme,
        }),
        [parent, overrides, theme]
    );
    return <UIVariantsContext.Provider value={value}>{children}</UIVariantsContext.Provider>;
}

export function useUIVariants(): RecipeContextValue {
    return useContext(UIVariantsContext);
}

/**
 * Resolve a recipe to its final class string, app overrides included.
 *
 * With no provider mounted this is exactly the recipe's own output, so a
 * component using it costs nothing until someone actually customises.
 */
export function useRecipe<V extends Record<string, Record<string, ClassValue>>>(
    recipe: Recipe<V>,
    props?: Parameters<Recipe<V>['resolve']>[0]
): string {
    const ctx = useUIVariants();
    return useMemo(
        () =>
            applyOverride(
                recipe as Recipe<Record<string, Record<string, ClassValue>>>,
                recipe.resolve(props),
                props as Record<string, unknown> | undefined,
                ctx
            ),
        [recipe, props, ctx]
    );
}
